/* R148 · Feb 2026 — Force Check-in modal.
 * When workspace hr_settings.force_checkin=true AND the current user
 * hasn't clocked in today AND isn't on leave/WFH/holiday, a blocking
 * modal is rendered on top of the app so they must clock in first.
 *
 * Kept lightweight: polls /attendance/today + /hr-settings on mount
 * and after each punch success. */
import { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlarmClockCheck, MapPin, Wifi } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { collectPunchMeta } from "@/lib/punchMeta";
import { apiErrorMessage } from "@/lib/apiError";
import StalePunchDialog from "@/components/attendance/StalePunchDialog";

export default function ForceCheckinModal() {
  const { activeWorkspaceId: wsId, user, impersonation } = useAuth();
  const [needed, setNeeded] = useState(false);
  const [today, setToday] = useState(null);
  const [locations, setLocations] = useState([]);
  const [wifiSsid, setWifiSsid] = useState("");
  const [enforcing, setEnforcing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [stale, setStale] = useState(null);
  const [pendingMeta, setPendingMeta] = useState(null);

  const check = useCallback(async () => {
    if (!wsId || !user?.id) return;
    // Feb 2026 — Bypass the force-checkin gate while an admin is
    // impersonating this user. Impersonation is for QA / access
    // verification; forcing a real punch would pollute attendance data
    // and block the tester from reaching the pages they need to audit.
    if (impersonation) { setNeeded(false); return; }
    try {
      const [{ data: st }, { data: t }] = await Promise.all([
        api.get(`/workspaces/${wsId}/hr-settings`).catch(() => ({ data: {} })),
        api.get(`/workspaces/${wsId}/attendance/today`).catch(() => ({ data: null })),
      ]);
      if (!st?.force_checkin) { setNeeded(false); return; }
      setEnforcing(!!st.enforce_location_checkin);
      setToday(t);
      // R148b · With multi-punch we no longer treat "clock_out present"
      // as "done for the day". The user must have an OPEN punch to
      // satisfy the requirement — i.e. currently clocked in.
      const clockedIn = !!t?.open_punch;
      const isOff = !!t?.holiday || !!t?.weekly_off || t?.record?.source === "leave";
      const mustCheckIn = !clockedIn && !isOff;
      setNeeded(mustCheckIn);
      if (mustCheckIn && st.enforce_location_checkin) {
        api.get(`/workspaces/${wsId}/me/work-locations`).then((r) => setLocations(r.data || [])).catch(() => setLocations([]));
      }
    } catch { /* ignore */ }
  }, [wsId, user?.id, impersonation]);

  useEffect(() => { check(); }, [check]);

  useEffect(() => {
    const handler = () => check();
    window.addEventListener("acq-refresh-compliance", handler);
    return () => window.removeEventListener("acq-refresh-compliance", handler);
  }, [check]);

  const punch = async () => {
    setBusy(true);
    try {
      const meta = await collectPunchMeta({ requireLocation: true });
      if (wifiSsid.trim()) meta.wifi_ssid = wifiSsid.trim();
      try {
        await api.post(`/workspaces/${wsId}/attendance/clock-in`, meta);
      } catch (err) {
        // R148b · If a stale open punch is blocking us, prompt the user
        // via the StalePunchDialog and retry.
        if (err?.response?.status === 409 && err?.response?.data?.detail?.code === "open_punch_exists") {
          setStale(err.response.data.detail.open_punch);
          setPendingMeta(meta);
          return;
        } else { throw err; }
      }
      toast.success("Clocked in — have a great day!");
      setNeeded(false);
      window.dispatchEvent(new Event("acq-refresh-compliance"));
    } catch (e) {
      if (e?.message && !e.response) toast.error(e.message);
      else toast.error(apiErrorMessage(e, "Check-in failed"));
    } finally { setBusy(false); }
  };

  if (!needed) return null;

  const suggestedSsids = Array.from(new Set(locations.flatMap((l) => l.allowed_wifi_ssids || [])));

  return (
    <Dialog open>
      <DialogContent
        className="max-w-md"
        hideClose
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="force-checkin-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlarmClockCheck className="w-5 h-5 text-emerald-600" />
            Check in to continue
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Your workspace requires all members to check in before using the app.
            Please clock in now to unlock your dashboard, tasks and everything else.
          </p>
          {enforcing && (
            <div className="border border-primary/20 bg-primary/5 p-3 space-y-2 text-xs">
              <div className="flex items-center gap-1.5 font-semibold"><MapPin className="w-3.5 h-3.5" /> Location required</div>
              {locations.length === 0 ? (
                <p className="text-muted-foreground">
                  No work locations assigned to you yet. Ask your HR admin to assign one under Attendance → HR Settings → Work Locations.
                </p>
              ) : (
                <>
                  <p className="text-muted-foreground">You can clock in from any of these locations:</p>
                  <ul className="list-disc ml-4">
                    {locations.map((l) => (
                      <li key={l.id}>
                        {l.name}{l.address ? ` · ${l.address}` : ""}
                        {(l.allowed_wifi_ssids || []).length > 0 && <span className="ml-1 text-muted-foreground">(WiFi: {l.allowed_wifi_ssids.join(", ")})</span>}
                      </li>
                    ))}
                  </ul>
                  {suggestedSsids.length > 0 && (
                    <div>
                      <div className="flex items-center gap-1.5 mt-2 mb-1"><Wifi className="w-3.5 h-3.5" /> WiFi network (optional)</div>
                      <Input
                        value={wifiSsid}
                        onChange={(e) => setWifiSsid(e.target.value)}
                        placeholder={`e.g. ${suggestedSsids[0]}`}
                        className="h-8 text-xs"
                        data-testid="force-checkin-ssid"
                      />
                      <p className="text-[10px] text-muted-foreground mt-1">Enter it if you&apos;re indoors and GPS is spotty.</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          <Button
            className="rounded-none w-full h-10 text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={punch}
            disabled={busy}
            data-testid="force-checkin-btn"
          >
            {busy ? "Checking in…" : "Check in now"}
          </Button>
        </div>
      </DialogContent>
      {stale && (
        <StalePunchDialog
          openPunch={stale}
          onCancel={() => { setStale(null); setPendingMeta(null); }}
          onConfirm={async (isoOut) => {
            try {
              await api.post(`/workspaces/${wsId}/attendance/close-open-punch`, {
                punch_id: stale.id, clock_out_at: isoOut,
              });
              await api.post(`/workspaces/${wsId}/attendance/clock-in`, pendingMeta || {});
              toast.success("Previous session closed. New session started — have a great day!");
              setStale(null);
              setPendingMeta(null);
              setNeeded(false);
              window.dispatchEvent(new Event("acq-refresh-compliance"));
            } catch (e) { throw e; }
          }}
        />
      )}
    </Dialog>
  );
}
